import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { ApiError, type ApiFieldErrors } from '../../lib/api';
import {
  getReplayParticipants,
  searchReplayOptions,
  submitPublicCorrection,
  type EvidenceInput,
  type PublicCorrectionCard,
  type PublicCorrectionInput,
} from '../../lib/manualCorrectionsApi';
import { SmartCaptchaField } from './SmartCaptchaField';

type EvidenceRow = EvidenceInput & { id: number };

const inputClass =
  'mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-ring/30';
const labelClass = 'block text-sm font-semibold';
const hintClass = 'mt-1 block text-xs font-normal text-muted-foreground';
const squadTagPattern = /^[A-Za-z0-9_]+$/;
const squadTagError = 'Допустимы только латинские буквы, цифры и символ «_».';

const FieldError = ({ messages }: { messages?: string[] }) =>
  messages?.length ? <p className="mt-1 text-sm text-destructive">{messages.join(' ')}</p> : null;

const textValue = (data: FormData, key: string): string => String(data.get(key) ?? '').trim();

const replayNameFromUrl = (value: string): string => {
  try {
    return new URL(value.trim()).searchParams.get('file')?.trim() ?? '';
  } catch {
    return '';
  }
};

export function ManualCorrectionForm({
  onSubmitted,
}: {
  onSubmitted: (correction: PublicCorrectionCard) => void;
}): JSX.Element {
  const [weapon, setWeapon] = useState('');
  const [distance, setDistance] = useState('');
  const [distanceAcknowledged, setDistanceAcknowledged] = useState(false);
  const [replayUrl, setReplayUrl] = useState('');
  const [debouncedReplayName, setDebouncedReplayName] = useState('');
  const [evidence, setEvidence] = useState<EvidenceRow[]>([]);
  const [nextEvidenceId, setNextEvidenceId] = useState(1);
  const [clientError, setClientError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const [captchaError, setCaptchaError] = useState<string | null>(null);
  const [killerSquadError, setKillerSquadError] = useState<string | null>(null);
  const [victimSquadError, setVictimSquadError] = useState<string | null>(null);
  const [killerSquad, setKillerSquad] = useState('');
  const [victimSquad, setVictimSquad] = useState('');
  const [killerIsSolo, setKillerIsSolo] = useState(false);
  const [victimIsSolo, setVictimIsSolo] = useState(false);
  const captchaSiteKey = (
    import.meta.env.VITE_YANDEX_SMARTCAPTCHA_CLIENT_KEY as string | undefined
  )?.trim() ?? '';
  const localCaptcha =
    import.meta.env.DEV &&
    import.meta.env.VITE_WEBSTAT_LOCAL_CAPTCHA === 'true';

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedReplayName(replayNameFromUrl(replayUrl)),
      250
    );
    return () => window.clearTimeout(timer);
  }, [replayUrl]);

  const replayQuery = useQuery({
    queryKey: ['manual-correction-replays', debouncedReplayName],
    queryFn: () => searchReplayOptions(debouncedReplayName),
    enabled: debouncedReplayName.trim().length >= 2,
    staleTime: 30_000,
  });
  const matchedReplay = useMemo(
    () =>
      replayQuery.data?.results.find(
        (item) =>
          item.replay_url === replayUrl.trim() ||
          item.replay_name === replayNameFromUrl(replayUrl)
      ),
    [replayUrl, replayQuery.data]
  );
  const participantQuery = useQuery({
    queryKey: ['manual-correction-participants', matchedReplay?.id],
    queryFn: () => getReplayParticipants(matchedReplay?.id ?? 0),
    enabled: Boolean(matchedReplay),
    staleTime: 30_000,
  });

  const resetCaptcha = () => {
    setCaptchaToken('');
    setCaptchaError(null);
    setCaptchaResetKey((current) => current + 1);
  };

  const mutation = useMutation({
    mutationFn: submitPublicCorrection,
    onSuccess: (correction) => onSubmitted(correction),
    onError: resetCaptcha,
  });
  const serverErrors: ApiFieldErrors =
    mutation.error instanceof ApiError ? mutation.error.errors : {};

  const updateEvidence = (id: number, patch: Partial<EvidenceInput>) => {
    setEvidence((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const addEvidence = () => {
    if (evidence.length >= 5) return;
    setEvidence((current) => [...current, { id: nextEvidenceId, url: '', label: '' }]);
    setNextEvidenceId((current) => current + 1);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (mutation.isPending) return;
    setClientError(null);

    const form = event.currentTarget;
    const formData = new FormData(form);
    const nextKillerSquadError =
      killerSquad && !squadTagPattern.test(killerSquad) ? squadTagError : null;
    const nextVictimSquadError =
      victimSquad && !squadTagPattern.test(victimSquad) ? squadTagError : null;
    setKillerSquadError(nextKillerSquadError);
    setVictimSquadError(nextVictimSquadError);
    if (nextKillerSquadError || nextVictimSquadError) return;
    if (!form.reportValidity()) return;
    if ((distance.trim() === '' || Number(distance) === 0) && !distanceAcknowledged) {
      setClientError('Подтвердите, что дистанция будет уточнена проверяющим или принята как 0.');
      return;
    }
    const incompleteEvidence = evidence.some((item) => !item.url.trim() && Boolean(item.label?.trim()));
    if (incompleteEvidence) {
      setClientError('Для описания доказательства укажите HTTP-ссылку.');
      return;
    }
    if (!captchaToken) {
      setClientError('Пройдите проверку CAPTCHA перед отправкой заявки.');
      return;
    }

    const description = textValue(formData, 'description');
    const payload: PublicCorrectionInput = {
      smartcaptcha_token: captchaToken,
      website: textValue(formData, 'website'),
      replay_url: textValue(formData, 'replay_url'),
      event_time: textValue(formData, 'event_time'),
      claimed_killer_name: textValue(formData, 'claimed_killer_name'),
      claimed_killer_squad: textValue(formData, 'claimed_killer_squad'),
      claimed_killer_is_solo: killerIsSolo,
      claimed_victim_name: textValue(formData, 'claimed_victim_name'),
      claimed_victim_squad: textValue(formData, 'claimed_victim_squad'),
      claimed_victim_is_solo: victimIsSolo,
      claimed_weapon_or_method: weapon.trim(),
      claimed_distance: distance.trim() === '' ? null : Number(distance),
      ...(description ? { description } : {}),
      evidence: evidence
        .filter((item) => item.url.trim())
        .map(({ url, label }) => ({ url: url.trim(), label: label?.trim() ?? '' })),
      regulation_accepted: true,
    };
    mutation.mutate(payload);
  };

  const participantOptions = participantQuery.data?.results ?? [];

  return (
    <section aria-labelledby="correction-form-title" className="rounded-lg border border-border bg-card p-4 shadow-sm sm:p-6">
      <div className="max-w-3xl">
        <h2 id="correction-form-title" className="text-2xl font-semibold">Подать заявку</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Одна заявка описывает один спорный фраг. Поля со звёздочкой обязательны.
        </p>
      </div>

      <form className="mt-6 space-y-6" onSubmit={handleSubmit} noValidate>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-[10000px] h-px w-px overflow-hidden"
        >
          <label>
            Website
            <input
              name="website"
              tabIndex={-1}
              autoComplete="off"
              maxLength={200}
            />
          </label>
        </div>
        <fieldset className="grid gap-4 sm:grid-cols-2">
          <legend className="col-span-full mb-2 text-lg font-semibold">Бой и событие</legend>
          <label className={labelClass}>
            Тайминг события *
            <input
              aria-label="Тайминг события *"
              aria-describedby="event-time-hint"
              className={inputClass}
              name="event_time"
              required
              maxLength={8}
              pattern="(?:(?:[0-9]{1,2}):)?(?:[0-5]?[0-9]):(?:[0-5][0-9])"
              placeholder="12:34 или 01:12:34"
            />
            <span id="event-time-hint" className={hintClass}>
              Согласно таймингу события на реплее.
            </span>
            <FieldError messages={serverErrors.event_time} />
          </label>
          <label className={labelClass}>
            Ссылка на реплей *
            <input
              aria-label="Ссылка на реплей *"
              aria-describedby="replay-url-hint"
              className={inputClass}
              type="url"
              inputMode="url"
              name="replay_url"
              required
              maxLength={1000}
              list="manual-correction-replays"
              value={replayUrl}
              onChange={(event) => setReplayUrl(event.target.value)}
              autoComplete="off"
            />
            <span id="replay-url-hint" className={hintClass}>
              Ссылка на реплей из Replay Viewer&apos;а
            </span>
            <datalist id="manual-correction-replays">
              {replayQuery.data?.results.map((replay) => (
                <option key={replay.id} value={replay.replay_url}>
                  {replay.replay_name}
                </option>
              ))}
            </datalist>
            {replayQuery.data && debouncedReplayName && !matchedReplay ? (
              <span role="status" className="mt-1 block text-xs font-normal text-muted-foreground">
                Реплей не найден в подсказках. Валидную ссылку всё равно можно отправить.
              </span>
            ) : null}
            <FieldError messages={serverErrors.replay_url} />
          </label>
        </fieldset>

        <fieldset className="grid gap-4 sm:grid-cols-2">
          <legend className="col-span-full mb-2 text-lg font-semibold">Участники</legend>
          <label className={labelClass}>
            Позывной автора фрага *
            <input
              aria-label="Позывной автора фрага"
              aria-describedby="claimed-killer-name-hint"
              className={inputClass}
              name="claimed_killer_name"
              required
              maxLength={50}
              list="manual-correction-participants"
            />
            <span id="claimed-killer-name-hint" className={hintClass}>
              Только позывной, без тега, например для [DWI]Krupa надо указать Krupa.
            </span>
            <FieldError messages={serverErrors.claimed_killer_name} />
          </label>
          <div className={labelClass}>
            <label className="block">
              Отряд автора фрага{killerIsSolo ? '' : ' *'}
              <input
                aria-label="Отряд автора фрага"
                aria-describedby="claimed-killer-squad-hint"
                aria-invalid={Boolean(killerSquadError || serverErrors.claimed_killer_squad)}
                className={inputClass}
                name="claimed_killer_squad"
                required={!killerIsSolo}
                disabled={killerIsSolo}
                maxLength={50}
                pattern="[A-Za-z0-9_]+"
                value={killerSquad}
                onChange={(event) => {
                  setKillerSquad(event.target.value);
                  setKillerSquadError(
                    event.target.value && !squadTagPattern.test(event.target.value)
                      ? squadTagError
                      : null
                  );
                }}
              />
            </label>
            <label className="mt-2 flex items-center gap-2 text-sm font-normal">
              <input
                type="checkbox"
                aria-label="Одиночка: автор фрага"
                checked={killerIsSolo}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setKillerIsSolo(checked);
                  if (checked) {
                    setKillerSquad('');
                    setKillerSquadError(null);
                  }
                }}
              />
              <span>Одиночка</span>
            </label>
            <span id="claimed-killer-squad-hint" className={hintClass}>
              Только тег отряда без квадратных скобок. Для игрока без отряда отметьте «Одиночка».
            </span>
            <FieldError
              messages={killerSquadError ? [killerSquadError] : serverErrors.claimed_killer_squad}
            />
          </div>
          <label className={labelClass}>
            Позывной жертвы *
            <input
              aria-label="Позывной жертвы"
              aria-describedby="claimed-victim-name-hint"
              className={inputClass}
              name="claimed_victim_name"
              required
              maxLength={50}
              list="manual-correction-participants"
            />
            <span id="claimed-victim-name-hint" className={hintClass}>
              Только позывной, без тега, например для [DWI]Krupa надо указать Krupa.
            </span>
            <FieldError messages={serverErrors.claimed_victim_name} />
          </label>
          <div className={labelClass}>
            <label className="block">
              Отряд жертвы
              <input
                aria-label="Отряд жертвы"
                aria-describedby="claimed-victim-squad-hint"
                aria-invalid={Boolean(victimSquadError || serverErrors.claimed_victim_squad)}
                className={inputClass}
                name="claimed_victim_squad"
                disabled={victimIsSolo}
                maxLength={50}
                pattern="[A-Za-z0-9_]+"
                value={victimSquad}
                onChange={(event) => {
                  setVictimSquad(event.target.value);
                  setVictimSquadError(
                    event.target.value && !squadTagPattern.test(event.target.value)
                      ? squadTagError
                      : null
                  );
                }}
              />
            </label>
            <label className="mt-2 flex items-center gap-2 text-sm font-normal">
              <input
                type="checkbox"
                aria-label="Одиночка: жертва"
                checked={victimIsSolo}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setVictimIsSolo(checked);
                  if (checked) {
                    setVictimSquad('');
                    setVictimSquadError(null);
                  }
                }}
              />
              <span>Одиночка</span>
            </label>
            <span id="claimed-victim-squad-hint" className={hintClass}>
              Для одиночки отметьте «Одиночка». Поле также можно оставить пустым для класса техники.
            </span>
            <FieldError
              messages={victimSquadError ? [victimSquadError] : serverErrors.claimed_victim_squad}
            />
          </div>
          <datalist id="manual-correction-participants">
            {participantOptions.map((participant) => (
              <option key={`${participant.nick}-${participant.squad}`} value={participant.nick}>
                {participant.squad}
              </option>
            ))}
          </datalist>
        </fieldset>

        <fieldset className="grid gap-4 sm:grid-cols-2">
          <legend className="col-span-full mb-2 text-lg font-semibold">Обстоятельства</legend>
          <label className={labelClass}>
            Оружие или способ убийства
            <input
              aria-label="Оружие или способ убийства"
              aria-describedby="claimed-weapon-hint"
              className={inputClass}
              name="claimed_weapon_or_method"
              maxLength={100}
              value={weapon}
              onChange={(event) => setWeapon(event.target.value)}
            />
            <span id="claimed-weapon-hint" className={hintClass}>
              Желательно вставить класс оружия, как он указан на реплее. Например, для события
              00:52:15 [RM]Shaitan → [KPblM]nunel: SVD, 722м рекомендуется указать SVD.
            </span>
            <FieldError messages={serverErrors.claimed_weapon_or_method} />
          </label>
          <label className={labelClass}>
            Дистанция, м
            <input
              className={inputClass}
              type="number"
              name="claimed_distance"
              min={0}
              step={1}
              value={distance}
              onChange={(event) => {
                setDistance(event.target.value);
                setDistanceAcknowledged(false);
              }}
            />
            <FieldError messages={serverErrors.claimed_distance} />
          </label>

          {!weapon.trim() ? (
            <div role="status" className="sm:col-span-2 rounded-md border border-primary/40 bg-primary/10 p-3 text-sm">
              Оружие не указано. Если модератор не сможет его уточнить, будет использован
              &quot;Бинокль&quot; и фраг попадёт в Пехотную статистику.
            </div>
          ) : null}

          {distance.trim() === '' || Number(distance) === 0 ? (
            <label className="sm:col-span-2 flex items-start gap-3 rounded-md border border-border p-3 text-sm">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-primary"
                checked={distanceAcknowledged}
                onChange={(event) => setDistanceAcknowledged(event.target.checked)}
              />
              <span>Понимаю, что дистанцию уточнит проверяющий или подтвердит значение 0.</span>
            </label>
          ) : null}

          <label className={`${labelClass} sm:col-span-2`}>
            Описание обстоятельств
            <textarea className={`${inputClass} min-h-28 resize-y`} name="description" maxLength={2000} />
            <FieldError messages={serverErrors.description} />
          </label>
        </fieldset>

        <fieldset className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <legend className="text-lg font-semibold">Доказательства</legend>
            <button
              type="button"
              onClick={addEvidence}
              disabled={evidence.length >= 5}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm font-semibold hover:border-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              Добавить ссылку ({evidence.length}/5)
            </button>
          </div>
          {!evidence.length ? (
            <p role="status" className="text-sm text-muted-foreground">
              Ссылки необязательны, но без них решение будет зависеть от достаточности остальных данных.
            </p>
          ) : null}
          {evidence.map((row, index) => (
            <div key={row.id} className="grid gap-3 rounded-md border border-border p-3 sm:grid-cols-[1fr,0.7fr,auto]">
              <label className={labelClass}>
                HTTP-ссылка {index + 1}
                <input
                  className={inputClass}
                  type="url"
                  inputMode="url"
                  maxLength={1000}
                  pattern="https?://.*"
                  value={row.url}
                  onChange={(event) => updateEvidence(row.id, { url: event.target.value })}
                />
              </label>
              <label className={labelClass}>
                Краткое описание
                <input
                  className={inputClass}
                  maxLength={100}
                  value={row.label}
                  onChange={(event) => updateEvidence(row.id, { label: event.target.value })}
                />
              </label>
              <button
                type="button"
                onClick={() => setEvidence((current) => current.filter((item) => item.id !== row.id))}
                className="self-end rounded-md border border-border px-3 py-2 text-sm font-semibold hover:border-destructive hover:text-destructive"
                aria-label={`Удалить ссылку ${index + 1}`}
              >
                Удалить
              </button>
            </div>
          ))}
          <FieldError messages={serverErrors.evidence} />
        </fieldset>

        <label className="flex items-start gap-3 rounded-md border border-border p-4 text-sm">
          <input type="checkbox" name="regulation_accepted" required className="mt-1 h-4 w-4 accent-primary" />
          <span>Я прочитал Регламент и подтверждаю согласие с ним.</span>
        </label>
        <FieldError messages={serverErrors.regulation_accepted} />

        <SmartCaptchaField
          siteKey={captchaSiteKey}
          localMode={localCaptcha}
          resetKey={captchaResetKey}
          error={captchaError}
          onSuccess={(token) => {
            setCaptchaToken(token);
            setCaptchaError(null);
          }}
          onExpired={() => {
            setCaptchaToken('');
            setCaptchaError('Срок действия проверки CAPTCHA истёк. Пройдите её ещё раз.');
            setCaptchaResetKey((current) => current + 1);
          }}
          onWidgetError={() => {
            setCaptchaToken('');
            setCaptchaError(
              'Не удалось загрузить CAPTCHA. Проверьте соединение и повторите попытку.'
            );
            if (!captchaError) {
              setCaptchaResetKey((current) => current + 1);
            }
          }}
          onRetry={resetCaptcha}
        />
        <FieldError messages={serverErrors.smartcaptcha_token} />

        {clientError ? <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 p-3">{clientError}</p> : null}
        {mutation.isError ? (
          <div role="alert" className="rounded-md border border-destructive/40 bg-destructive/10 p-3">
            {mutation.error instanceof ApiError ? mutation.error.message : 'Не удалось отправить заявку. Повторите позже.'}
          </div>
        ) : null}
        <FieldError messages={serverErrors.non_field_errors} />

        <button
          type="submit"
          disabled={
            mutation.isPending ||
            !captchaToken ||
            (!captchaSiteKey && !localCaptcha)
          }
          className="inline-flex min-h-11 items-center justify-center rounded-md bg-primary px-5 py-2.5 font-semibold text-primary-foreground transition hover:brightness-95 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {mutation.isPending ? 'Отправляем…' : 'Отправить заявку'}
        </button>
      </form>
    </section>
  );
}
